import { VideoJsPlayer } from '@app/core/video-player/video-js/VideoJsPlayer';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { alpha, Box, IconButton } from '@mui/material';
import { useState } from 'react';

export interface EditCardRenderedVideoPreviewProps {
  playbackUrl: string;
  children: React.ReactNode;
}

/**
 * Wraps edit card thumbnail (or empty placeholder): on hover shows a centered play control;
 * activating it replaces the preview with an inline Video.js player for the rendered asset URL.
 */
export function EditCardRenderedVideoPreview({
  playbackUrl,
  children,
}: EditCardRenderedVideoPreviewProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  if (isPlaying) {
    return (
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16/9',
          width: '100%',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
          overflow: 'hidden',
          isolation: 'isolate',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            '& video': {
              cursor: 'pointer',
            },
          }}
        >
          <VideoJsPlayer
            src={playbackUrl}
            options={{
              autoplay: true,
              bigPlayButton: false,
              fluid: false,
              fill: true,
              controlBar: {
                pictureInPictureToggle: false,
              },
            }}
            sx={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        isolation: 'isolate',
        '&:hover': {
          cursor: 'pointer',
        },
        '&:hover .edit-card-rendered-play-overlay': {
          opacity: 1,
        },
      }}
    >
      {children}
      <IconButton
        type="button"
        className="edit-card-rendered-play-overlay"
        size="large"
        onClick={(event) => {
          event.stopPropagation();
          setIsPlaying(true);
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          opacity: 0,
          transition: (theme) =>
            theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
          zIndex: 2,
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.15),
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            bgcolor: 'background.paper',
            boxShadow: 2,
            borderRadius: '50%',
            width: (theme) => theme.spacing(7),
            height: (theme) => theme.spacing(7),
          }}
        >
          <PlayArrowRoundedIcon fontSize="large" />
        </Box>
      </IconButton>
    </Box>
  );
}
